import { useState, useMemo } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { FiGrid, FiList } from "react-icons/fi";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../auth/AuthProvider";
import { useCustomerId } from "../../hooks/useCustomerId";
import { Button } from "../../components";
import { PortfolioFilterBar } from "../../features/portfolio-dashboard/components/PortfolioFilterBar";

import { ProjectTile } from "../../features/projects/domain/types";
import { getAll, getByCustomer } from "../../api/projects";
import { ProjectCreateModal } from "../../features/projects/components/ProjectCreateModal";
import { ProjectsList } from "../../features/projects/components/ProjectsList";
import { ProjectGrid } from "../../features/projects/components/ProjectGrid";
type ViewMode = "tiles" | "list";

export default function ProjectsAll() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token, isAdmin, isPartner } = useAuth();
  const customerId = useCustomerId(token);
  const clientId = searchParams.get("client") || undefined;

  const [isAddOpen, setIsAddOpen] = useState(() => !!clientId);
  const [viewMode, setViewMode] = useState<ViewMode>("list");

  // Parse filter[key]=value from URL params
  const activeFilters = useMemo(() => {
    const filters: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      if (key.startsWith("filter[") && key.endsWith("]")) {
        const filterKey = key.slice(7, -1);
        filters[filterKey] = value;
      }
    });
    return filters;
  }, [searchParams]);

  const removeFilter = (key: string) => {
    const next = new URLSearchParams(searchParams);
    next.delete(`filter[${key}]`);
    setSearchParams(next);
  };

  const clearFilters = () => {
    const next = new URLSearchParams();
    // Keep non-filter params
    searchParams.forEach((value, key) => {
      if (!key.startsWith("filter[")) next.set(key, value);
    });
    setSearchParams(next);
  };

  // Fetch projects for the current user's customer ID
  const {
    data: projects = [],
    isLoading: projectsLoading,
    error: projectsError
  } = useQuery<ProjectTile[]>({
    queryKey: ["projects", customerId],
    queryFn: async () => {
      if (!token) return [];

      try {
        // Admin users use the admin endpoint; regular/partner users use the standard endpoint
        const data = isAdmin && customerId
          ? await getByCustomer(customerId, token)
          : await getAll(token);
        const projectsArray = Array.isArray(data) ? data : data.projects || [];

        return projectsArray.map((proj: any) => ({
          project_id: proj.project_id,
          name: proj.project_name ?? `Project ${proj.project_id}`,
          status: proj.existing_project_flag ? "Operating" : "Prospective",
          config_version: proj.config_version || 'v1.0',
          tech_type: proj.tech_type_id,
          eia_ba_code: proj.eia_ba_code,
        }));
      } catch (error) {
        console.error("Failed to fetch projects:", error);
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30,  // 30 minutes
    enabled: !!customerId && !!token,
  });

  // Apply client-side filtering based on URL filter params
  const filteredProjects = useMemo(() => {
    if (Object.keys(activeFilters).length === 0) return projects;
    return projects.filter((proj) => {
      for (const [key, value] of Object.entries(activeFilters)) {
        if (key === "lifecycle") {
          const isOperating = proj.status === "Operating";
          if (value === "existing" && !isOperating) return false;
          if (value === "future" && isOperating) return false;
        } else if (key === "tech") {
          if (proj.tech_type !== value) return false;
        } else if (key === "region") {
          if (proj.eia_ba_code !== value) return false;
        }
      }
      return true;
    });
  }, [projects, activeFilters]);

  const goToProject = (projectId: string, projectName: string) => {
    navigate(`/app/projects/${encodeURIComponent(projectId)}?name=${encodeURIComponent(projectName)}`);
  };

  // Handler for ProjectGrid component
  const handleProjectClick = (project: ProjectTile) => {
    goToProject(project.project_id, project.name);
  };

  return (
    <div className="pt-0 px-6 pb-6">
      {/* Add back link here */}
      <Link
        to={isPartner ? "/app/clients" : "/app/portfolios"}
        className="text-sm text-quantum-400 hover:underline"
      >
        {isPartner ? "\u2190 Back to Clients" : "\u2190 Back to Portfolios"}
      </Link>

      {/* Header with view toggle */}
      <div className="flex items-center justify-between mt-2 mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Projects</h1>

        <div className="flex items-center space-x-4">
          {/* Compare button for partners */}
          {isPartner && (
            <Link to="/app/compare">
              <Button variant="ghost" size="sm">
                Compare Projects
              </Button>
            </Link>
          )}

          {/* View toggle buttons */}
          <div className="bg-surface-600 rounded-md p-1 flex">
            <button
              onClick={() => setViewMode("tiles")}
              className={`px-3 py-1 rounded-md text-sm flex items-center ${viewMode === "tiles"
                ? "bg-surface-500 shadow-sm text-quantum-400"
                : "text-text-muted hover:text-text-primary"
                }`}
            >
              <FiGrid className="mr-1" />
              Tiles
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`px-3 py-1 rounded-md text-sm flex items-center ${viewMode === "list"
                ? "bg-surface-500 shadow-sm text-quantum-400"
                : "text-text-muted hover:text-text-primary"
                }`}
            >
              <FiList className="mr-1" />
              List
            </button>
          </div>
        </div>
      </div>

      {/* Active filter chips */}
      <PortfolioFilterBar
        filters={activeFilters}
        onRemove={removeFilter}
        onClear={clearFilters}
      />

      {projectsError ? (
        <div className="bg-health-500/10 border border-health-500/30 text-health-300 px-4 py-3 rounded">
          <p>Error loading projects: {(projectsError as Error).message}</p>
          <button
            onClick={() => navigate(0)}
            className="mt-2 text-sm underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      ) : viewMode === "list" ? (
        /* List view */
        <ProjectsList
          projects={filteredProjects}
          isLoading={projectsLoading}
          onProjectClick={goToProject}
          onCreateClick={() => setIsAddOpen(true)}
          showCreateItem={true}
          customerId={customerId || ""}  // Add this prop
        />
      ) : (
        /* Tile view - using ProjectGrid component */
        <ProjectGrid
          projects={filteredProjects}
          onProjectClick={handleProjectClick}
          onCreateClick={() => setIsAddOpen(true)}
          isLoading={projectsLoading}
          error={projectsError as Error | null}
        />
      )}

      {/* Use ProjectCreateModal */}
      <ProjectCreateModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        customerId={customerId || ""}
        clientId={clientId}
        afterCreate={(projectId, projectName) => goToProject(projectId, projectName)}
      />
    </div>
  );
}
